import { t } from "ttag";

import { EXTERNAL_LINK } from "@/config/external-links";
import { PHONE_NUMBERS } from "@/shared/models";
import { FunnelBrand } from "./funnel-brand";

interface FooterLink {
  label: string;
  /** Omit when we have no working destination yet — rendered in red as a TODO. */
  href?: string;
}

// Deduped: links that pointed at the same destination are merged into a single
// entry, and the CareValidate "Medical Consent" link was removed (not a partner).
const FOOTER_LINKS: FooterLink[][] = [
  [
    { label: t`Home`, href: EXTERNAL_LINK.HOME },
    { label: t`Contact Us`, href: "mailto:support@levelsrx.com" },
  ],
  [
    { label: t`Blogs`, href: EXTERNAL_LINK.BLOG },
    { label: t`Terms of Service`, href: EXTERNAL_LINK.TERMS_AND_CONDITIONS },
  ],
  [
    { label: t`Privacy Policy`, href: EXTERNAL_LINK.PRIVACY_AND_POLICY },
    {
      label: t`Telehealth Consent`,
      href: EXTERNAL_LINK.OPENLOOP_TELEHEALTH_CONSENT,
    },
  ],
];

/**
 * Long legal disclaimer + footer shown beneath every funnel step, matching the
 * levelsRx consultation page. Purely presentational; renders the provider/pharmacy
 * disclosures and the navy footer with policy links.
 */
export const FunnelFooter = () => (
  <footer className="w-full bg-white text-neutral-600">
    <div className="mx-auto max-w-4xl space-y-4 px-5 py-8 text-sm leading-relaxed">
      <p>
        {t`The assessment made available on the levelsRx website does not create a doctor-patient relationship between the individual completing the assessment and levelsRx. OpenLoop Health, a network of US-licensed doctors that adhere to rigorous medical protocols designed for patient safety, has established exclusionary criteria to determine if an individual does not qualify for GLP-1s. The answers an individual provides to the levelsRx assessment determine if the individual is screened out of eligibility for GLP-1 medication, and a OpenLoop Health clinician will meet with the individual after checkout to determine if they qualify for a prescription. OpenLoop Health clinicians retain the sole discretion to prescribe compounded GLP-1s to patients.`}
      </p>
      <p>
        {t`All claims and results referenced on this website reflect self-reported data from members enrolled in the levelsRx healthcare membership program, which includes telehealth consultations with licensed providers and, where clinically appropriate, compounded GLP-1 medications. Members reported their weight at initial intake and at regular intervals thereafter. Individual results vary and depend on factors including starting weight, program adherence, diet, exercise, and the clinical recommendations of the treating provider. Compounded medications are prepared by state-licensed 503A compounding pharmacies. These medications are not FDA approved and have not been evaluated by the FDA for safety, efficacy, or quality. The decision to prescribe compounded medications is an individualized clinical determination made by the treating provider based on the patient's medical history, current health status, and telehealth assessment. HealthPrime Holding LLC, LevelsRx does not manufacture or compound medications. Actual medication appearance may differ from website imagery.`}
      </p>
      <p>
        {t`Pharmacy Partners: Prescriptions are filled by independently owned and operated state-licensed 503A compounding pharmacies within the levelsRx pharmacy network. Each pharmacy maintains its own state board of pharmacy licenses and operates under applicable federal and state compounding regulations.`}
      </p>
      <p>
        {t`Individual results vary based on starting weight, medical history, program adherence, diet, and exercise. The weight loss figures referenced on this website are based on self-reported data from levelsRx members and are not derived from clinical trials or peer-reviewed studies. Not all members experience the same results. A prescription is never guaranteed and is at the sole discretion of the treating provider. Consult your healthcare provider before beginning any weight management program.`}
      </p>
      <p>
        {t`The levelsRx healthcare membership includes access to telehealth consultations with licensed providers and, where clinically appropriate, prescription fulfillment through the levelsRx pharmacy network. Medical services are provided by Usable Health, PC and its affiliated professional medical practices, independently owned and operated. HealthPrime Holding LLC, LevelsRx provides the technology platform and administrative services and does not provide medical services or make prescribing decisions. No data, photographs, claims, or other information presented on this website are derived from clinical trials or published peer-reviewed studies. All results and testimonials are representative of individual levelsRx member experiences only.`}
      </p>
      <p>
        {t`For support:`}{" "}
        <a
          href="mailto:support@levelsrx.com"
          className="text-primary-950 underline"
        >
          support@levelsrx.com
        </a>{" "}
        |{" "}
        <span className="text-primary-950">
          {PHONE_NUMBERS.CUSTOMER_SUPPORT.NUMBER}
        </span>
      </p>
    </div>

    <div className="bg-primary-950">
      <div className="mx-auto max-w-4xl px-5 py-8">
        <FunnelBrand theme="dark" />
        <p className="mt-3 max-w-sm text-sm text-white/90">
          {t`We maintain administrative, technical, and physical safeguards designed to protect your health information.`}
        </p>

        <nav className="mt-6 grid grid-cols-2 gap-x-8 gap-y-2 text-sm text-white sm:grid-cols-3">
          {FOOTER_LINKS.map((column, columnIndex) => (
            <ul key={columnIndex} className="space-y-2">
              {column.map((link) =>
                link.href ? (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      {link.label}
                    </a>
                  </li>
                ) : (
                  // No working URL yet — flagged in red for the team to review.
                  <li
                    key={link.label}
                    className="font-semibold text-secondary-300"
                  >
                    {link.label}
                  </li>
                ),
              )}
            </ul>
          ))}
        </nav>

        <p className="mt-6 text-xs leading-relaxed text-neutral-300">
          {t`All medical services, including clinical assessments, treatment planning, and prescribing decisions are provided by affiliated professional medical practices, which are independently owned and operated. levelsRx provides the technology platform and administrative coordination services that connect you with your healthcare provider. levelsRx does not provide medical services, does not employ healthcare providers, does not participate in clinical decisions, and does not practice medicine.`}
        </p>
      </div>
    </div>
  </footer>
);
